import { CampoDeCodigo } from "@/ui/CodigoEmPecas";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { cabeMaisUmDesafio, quantosAtivos } from "@nhomi/core";
import { nomeDaSessao, useSession } from "@/lib/auth";
import { useMe, usePlano } from "@/lib/plano";
import { watchMyChallenges, type Challenge } from "@/data/challenges";
import { dayKey } from "@/data/plates";
import { SemVaga } from "@/ui/SemVaga";
import { JoinError, joinByCode } from "@/data/challenges";
import { ErrorNote, TopBar } from "@/ui/primitives";

/**
 * Entrar com código.
 *
 * Serve às duas portas: `/entrar-codigo` (digitar) e `/convite/:code` (link
 * compartilhado no zap, que preenche e entra sozinho). O caminho de verdade,
 * em vez de hash, é o que permite isso virar Universal Link no iOS depois.
 */
export function JoinChallenge() {
  const { user } = useSession();
  const { config, plano, assinante } = usePlano();
  const me = useMe();
  const [meus, setMeus] = useState<Challenge[] | null>(null);

  /* A porta confere de novo, e não confia na Home ter escondido o botão:
     esta rota abre por link direto — e no caso do convite, é assim que
     ela SEMPRE abre. */
  useEffect(() => {
    if (!user) return;
    return watchMyChallenges(user.uid, setMeus);
  }, [user]);
  const navigate = useNavigate();
  const params = useParams<{ code?: string }>();

  const [code, setCode] = useState((params.code ?? "").toUpperCase());
  const [erro, setErro] = useState("");
  const [entrando, setEntrando] = useState(false);
  const auto = Boolean(params.code);

  useEffect(() => {
    // Chegou por link com código: entra sozinho, sem pedir para digitar
    // o que já está na URL.
    if (auto && user && !entrando) void entrar(params.code!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, user]);

  async function entrar(valor: string) {
    if (!user) return;
    setErro("");
    setEntrando(true);
    try {
      const cid = await joinByCode(
        {
          uid: user.uid,
          name: me?.name ?? nomeDaSessao(user),
          photo: me?.photo ?? "",
          email: user.email ?? "",
        },
        valor,
      );
      navigate(`/c/${cid}`, { replace: true });
    } catch (err) {
      setEntrando(false);
      setErro(
        err instanceof JoinError
          ? err.message
          : "Não deu pra entrar com esse código. Confere e tenta de novo.",
      );
    }
  }

  /* Espera a lista chegar antes de decidir: mostrar "sem vaga" com a
     lista ainda em branco recusaria quem tem vaga de sobra. */
  const hoje = dayKey();
  const ativos = quantosAtivos(meus ?? [], hoje);
  if (meus && !assinante && !cabeMaisUmDesafio(config, plano, meus, hoje)) {
    return <SemVaga ativos={ativos} limite={config.desafiosGratis} />;
  }

  return (
    <main className="screen">
      <TopBar title="Entrar num desafio" back="/" />

      <div className="pad">
        {erro ? <ErrorNote>{erro}</ErrorNote> : null}

        <div className="card">
          <CampoDeCodigo valor={code} aoMudar={setCode} autoFocus />
        </div>

        <p className="note center">Peça o código pra quem criou o desafio.</p>

        <button
          className="btn btn-primary"
          onClick={() => entrar(code)}
          disabled={entrando || code.length < 4}
        >
          {entrando ? "Entrando…" : "Entrar"}
        </button>
      </div>
    </main>
  );
}
